import { useEffect, useState, useCallback } from 'react';
import { Plus, Check, X, CheckSquare, Tray } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { useAuth, canEditIt } from '@/auth/AuthContext';
import { itApi, REQUEST_TYPES, STATUS_META } from './itApi';
import { DataTable } from '@/components/ui/DataTable';
import { formatDateTime } from '@/utils/datetime';

const fmtDate = (s) => (s ? formatDateTime(s) : '—');

export default function RequestCenter() {
  const { user } = useAuth();
  const admin = canEditIt(user);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ request_type: 'vlan_clearance', title: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (statusFilter !== 'all') params.status = statusFilter;
    itApi.requests(params).then(setRequests).catch(() => {});
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error('タイトルを入力してください'); return; }
    setSubmitting(true);
    try {
      await itApi.createRequest({
        request_type: form.request_type,
        title: form.title.trim(),
        details: { notes: form.notes },
      });
      toast.success('申請を送信しました');
      setShowCreate(false);
      setForm({ request_type: 'vlan_clearance', title: '', notes: '' });
      load();
    } catch {
      toast.error('送信に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const decide = async (req, action) => {
    try {
      await itApi.decide(req.id, action);
      const map = { approve: '承認しました', reject: '却下しました', fulfill: '完了にしました' };
      toast.success(map[action]);
      load();
    } catch {
      toast.error('処理に失敗しました');
    }
  };

  return (
    <div className="space-y-5" data-testid="request-center">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">申請センター</h1>
        <div className="flex items-center gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="request-status-filter" className="w-40 h-11 rounded-lg border-zinc-300">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">状態: すべて</SelectItem>
              <SelectItem value="pending">承認待ち</SelectItem>
              <SelectItem value="approved">承認済み</SelectItem>
              <SelectItem value="rejected">却下</SelectItem>
              <SelectItem value="fulfilled">完了</SelectItem>
            </SelectContent>
          </Select>
          <Button data-testid="new-request-btn" onClick={() => setShowCreate(true)} className="h-11 rounded-lg bg-teal-700 hover:bg-teal-800 shadow-sm">
            <Plus size={18} className="mr-1.5" weight="bold" /> 新規申請
          </Button>
        </div>
      </div>

      <DataTable
        dataTestId="request-table"
        rows={requests}
        rowKey={(r) => r.id}
        rowTestId={(r) => `request-row-${r.id}`}
        empty={{ icon: <Tray size={40} className="text-zinc-300 mx-auto mb-2" weight="duotone" />, text: '申請がありません', testid: 'request-empty' }}
        columns={[
          { key: 'request_type', header: '種別', cell: (r) => <span className="text-zinc-600 whitespace-nowrap">{r.request_type_label}</span> },
          { key: 'title', primary: true, header: 'タイトル', cell: (r) => (
            <span className="font-medium text-zinc-800">{r.title}{r.details?.notes && <span className="block text-xs text-zinc-400 font-normal mt-0.5">{r.details.notes}</span>}</span>
          ) },
          admin && { key: 'requester', header: '申請者', cell: (r) => <span className="text-zinc-600 whitespace-nowrap">{r.requester_name || r.requester_email}</span> },
          { key: 'status', header: '', cell: (r) => {
            const sm = STATUS_META[r.status] || STATUS_META.pending;
            return <Badge className={`${sm.cls} hover:${sm.cls}`} variant="outline">{sm.label}</Badge>;
          } },
          { key: 'created_at', header: '申請日時', cell: (r) => <span className="text-zinc-500 whitespace-nowrap">{fmtDate(r.created_at)}</span> },
          admin && { key: 'actions', actions: true, header: '', cell: (r) => (<>
            {r.status === 'pending' && (<>
              <Button size="sm" data-testid={`approve-${r.id}`} onClick={() => decide(r, 'approve')} className="h-9 bg-teal-700 hover:bg-teal-800"><Check size={15} className="mr-1" /> 承認</Button>
              <Button size="sm" variant="outline" data-testid={`reject-${r.id}`} onClick={() => decide(r, 'reject')} className="h-9 text-red-600 border-red-200 hover:bg-red-50"><X size={15} className="mr-1" /> 却下</Button>
            </>)}
            {r.status === 'approved' && (
              <Button size="sm" variant="outline" data-testid={`fulfill-${r.id}`} onClick={() => decide(r, 'fulfill')} className="h-9 text-emerald-700 border-emerald-200 hover:bg-emerald-50"><CheckSquare size={15} className="mr-1" /> 完了</Button>
            )}
          </>) },
        ]}
      />

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md" data-testid="create-request-dialog">
          <DialogHeader>
            <DialogTitle>新規申請</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-sm font-semibold text-zinc-700">申請種別</Label>
              <Select value={form.request_type} onValueChange={(v) => setForm((p) => ({ ...p, request_type: v }))}>
                <SelectTrigger data-testid="request-type-select" className="mt-1 h-11 rounded-lg border-zinc-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REQUEST_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-semibold text-zinc-700">タイトル</Label>
              <Input
                data-testid="request-title"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="例: 新規ノートPC用のIP割当"
                className="mt-1 h-11 rounded-lg border-zinc-300"
              />
            </div>
            <div>
              <Label className="text-sm font-semibold text-zinc-700">詳細・備考</Label>
              <Textarea
                data-testid="request-notes"
                value={form.notes}
                onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
                placeholder="必要な情報を記載してください"
                className="mt-1 rounded-lg border-zinc-300 min-h-[90px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>キャンセル</Button>
            <Button data-testid="submit-request-btn" disabled={submitting} onClick={submit} className="bg-teal-700 hover:bg-teal-800">送信</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
